import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { 
  Clock,
  Users,
  Phone,
  Calendar,
  CheckCircle,
  AlertCircle,
  Plane,
  User,
  Shield,
  TrendingUp,
  Activity,
  Home,
  Building
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Today = () => {
  const { user } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [todayActivities, setTodayActivities] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [yesterdayStats, setYesterdayStats] = useState(null);
  const [coverageTargets, setCoverageTargets] = useState({});
  const [endingSoonHours, setEndingSoonHours] = useState(1.0); // Default to 1 hour
  
  // Refs for scrolling to sections
  const activeNowRef = React.useRef(null);
  const onCallRef = React.useRef(null);
  const ptoRef = React.useRef(null);
  const allShiftsRef = React.useRef(null);

  useEffect(() => {
    fetchTodayData();
    fetchSystemSettings();
    
    // Update current time every second for accurate countdowns
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // Auto-refresh data every 60 seconds
    const refreshTimer = setInterval(() => {
      fetchTodayData(true);
    }, 60000);

    return () => {
      clearInterval(timer);
      clearInterval(refreshTimer);
    };
  }, []);

  const fetchSystemSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/system-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEndingSoonHours(response.data?.ending_soon_hours || 1.0);
    } catch (error) {
      console.error('Failed to fetch system settings:', error);
      // Keep default value of 1.0 hour
    }
  };

  const fetchTodayData = async (isAutoRefresh = false) => {
    try {
      if (isAutoRefresh) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      // Get today's date
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      
      // Get yesterday's date for comparison
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];
      
      // Fetch today's schedules, yesterday's schedules, staffing rules, and activities
      const [schedulesRes, yesterdayRes, staffingRulesRes, activitiesRes, activityTypesRes] = await Promise.all([
        axios.get(`${API}/schedules?date_from=${todayStr}&date_to=${todayStr}`, { headers }),
        axios.get(`${API}/schedules?date_from=${yesterdayStr}&date_to=${yesterdayStr}`, { headers }),
        axios.get(`${API}/staffing-rules`, { headers }).catch(() => ({ data: { blocks: [] } })),
        axios.get(`${API}/activities?start_date=${todayStr}&end_date=${todayStr}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/activity-types`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      const schedulesData = schedulesRes.data || [];
      const yesterdayData = yesterdayRes.data || [];
      const staffingData = staffingRulesRes.data || { blocks: [] };
      const activitiesData = activitiesRes.data || [];
      const activityTypesData = activityTypesRes.data || [];
      
      setSchedules(schedulesData);
      setTodayActivities(activitiesData);
      setActivityTypes(activityTypesData);
      
      // Build coverage targets from staffing rules blocks for today's day of week
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const todayDayName = dayNames[new Date().getDay()];
      const todayBlocks = (staffingData.blocks || []).filter(b => b.day_of_week === todayDayName);
      
      const targetsMap = {};
      todayBlocks.forEach(block => {
        const reqs = block.requirements || {};
        Object.entries(reqs).forEach(([role, count]) => {
          if (!targetsMap[role]) targetsMap[role] = [];
          targetsMap[role].push({
            start_time: block.start_time || '00:00',
            end_time: block.end_time || '23:59',
            target_count: count,
          });
        });
      });
      setCoverageTargets(targetsMap);
      
      // Calculate yesterday's stats at this time
      if (yesterdayData.length > 0) {
        const currentHour = currentTime.getHours();
        const currentMinute = currentTime.getMinutes();
        const currentTimeMinutes = currentHour * 60 + currentMinute;
        
        const yesterdayActiveShifts = yesterdayData.filter(shift => {
          const [startHour, startMin] = shift.start_time.split(':').map(Number);
          const [endHour, endMin] = shift.end_time.split(':').map(Number);
          const shiftStartMinutes = startHour * 60 + startMin;
          const shiftEndMinutes = endHour * 60 + endMin;
          return currentTimeMinutes >= shiftStartMinutes && currentTimeMinutes < shiftEndMinutes;
        });
        
        // Count by role
        const yesterdayByRole = {};
        yesterdayActiveShifts.forEach(shift => {
          const role = shift.role || 'unknown';
          yesterdayByRole[role] = (yesterdayByRole[role] || 0) + 1;
        });
        
        setYesterdayStats({
          totalScheduled: yesterdayData.length,
          activeAtThisTime: yesterdayActiveShifts.length,
          byRole: yesterdayByRole
        });
      }
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch today data:', error);
      if (!isAutoRefresh) {
        toast.error('Failed to load today\'s schedule');
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getCurrentTimeStr = () => {
    return currentTime.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const isShiftActive = (schedule) => {
    const now = currentTime;
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    
    const [startHour, startMin] = schedule.start_time.split(':').map(Number);
    const [endHour, endMin] = schedule.end_time.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    let endMinutes = endHour * 60 + endMin;
    
    // Handle overnight shifts
    if (endMinutes <= startMinutes) {
      endMinutes += 24 * 60;
    }
    
    return currentMinutes >= startMinutes && currentMinutes < endMinutes;
  };

  const getUserForSchedule = (schedule) => {
    // Backend enriches schedules with 'user' object, use that directly
    if (schedule.user) {
      return {
        name: schedule.user.name || 'Unknown',
        email: schedule.user.email || '',
        // Support both new fields and legacy phone field
        work_phone: schedule.user.work_phone || schedule.user.phone || '',
        home_phone: schedule.user.home_phone || ''
      };
    }
    // Fallback for any non-enriched schedules
    return { name: 'Unknown', email: '', work_phone: '', home_phone: '' };
  };

  const getRoleLabel = (role) => {
    const labels = {
      'tier1': 'Tier 1',
      'tier2': 'Tier 2',
      'team_lead': 'Team Lead',
      'teamlead': 'Team Lead',
      'manager': 'Manager',
      'admin': 'Admin',
      'solution_engineer': 'Solution Engineer'
    };
    return labels[role] || role;
  };

  const getRoleColor = (role) => {
    const colors = {
      'tier1': 'bg-green-100 text-green-800',
      'tier2': 'bg-blue-100 text-blue-800',
      'team_lead': 'bg-purple-100 text-purple-800',
      'teamlead': 'bg-purple-100 text-purple-800',
      'manager': 'bg-indigo-100 text-indigo-800',
      'admin': 'bg-red-100 text-red-800',
      'solution_engineer': 'bg-teal-100 text-teal-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const getTimeUntilEnd = (endTimeStr) => {
    if (!endTimeStr) return null;
    
    const [hours, minutes] = endTimeStr.split(':').map(Number);
    const endTime = new Date(currentTime);
    endTime.setHours(hours, minutes, 0, 0);
    
    const diffMs = endTime - currentTime;
    if (diffMs <= 0) return null;
    
    const diffMinutes = Math.floor(diffMs / 60000);
    const displayHours = Math.floor(diffMinutes / 60);
    const displayMinutes = diffMinutes % 60;
    
    if (displayHours > 0) {
      return `${displayHours}h ${displayMinutes}m`;
    }
    return `${displayMinutes}m`;
  };

  const getTimeUntilEndColor = (endTimeStr) => {
    if (!endTimeStr) return 'text-gray-600';
    
    const [hours, minutes] = endTimeStr.split(':').map(Number);
    const endTime = new Date(currentTime);
    endTime.setHours(hours, minutes, 0, 0);
    
    const diffMs = endTime - currentTime;
    const diffMinutes = Math.floor(diffMs / 60000);
    
    if (diffMinutes <= 30) return 'text-red-600 font-semibold';
    if (diffMinutes <= 120) return 'text-yellow-600 font-semibold';
    return 'text-green-600';
  };

  const getUpcomingChanges = () => {
    const now = currentTime;
    const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);
    const configuredHoursFromNow = new Date(now.getTime() + endingSoonHours * 60 * 60 * 1000);
    
    const starting = [];
    const ending = [];
    
    schedules.forEach(shift => {
      if (!shift.start_time || !shift.end_time) return;
      
      const [startHour, startMin] = shift.start_time.split(':').map(Number);
      const [endHour, endMin] = shift.end_time.split(':').map(Number);
      
      const startTime = new Date(now);
      startTime.setHours(startHour, startMin, 0, 0);
      
      const endTime = new Date(now);
      endTime.setHours(endHour, endMin, 0, 0);
      
      // Starting soon (keep at 1 hour)
      if (startTime > now && startTime <= oneHourFromNow) {
        const minutesUntil = Math.floor((startTime - now) / 60000);
        starting.push({ ...shift, minutesUntil });
      }
      
      // Ending soon (use configured hours)
      if (endTime > now && endTime <= configuredHoursFromNow) {
        const minutesUntil = Math.floor((endTime - now) / 60000);
        ending.push({ ...shift, minutesUntil });
      }
    });
    
    return { starting: starting.sort((a, b) => a.minutesUntil - b.minutesUntil), ending: ending.sort((a, b) => a.minutesUntil - b.minutesUntil) };
  };

  const getTimeSinceUpdate = () => {
    const diffMs = currentTime - lastUpdated;
    const diffSeconds = Math.floor(diffMs / 1000);
    
    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    const diffMinutes = Math.floor(diffSeconds / 60);
    return `${diffMinutes}m ago`;
  };

  const getCoverageGaps = () => {
    // Get current time to find applicable targets
    const now = new Date();
    const currentTime = now.toTimeString().slice(0, 5); // "HH:MM" format
    
    // Find applicable targets for current time
    const applicableTargets = {};
    
    Object.entries(coverageTargets).forEach(([role, targets]) => {
      // targets might be a single number (legacy) or array of time-based targets
      if (typeof targets === 'number') {
        applicableTargets[role] = targets;
      } else if (Array.isArray(targets)) {
        // Find target for current time window
        const matchingTarget = targets.find(t => {
          return currentTime >= t.start_time && currentTime <= t.end_time;
        });
        if (matchingTarget) {
          applicableTargets[role] = matchingTarget.target_count;
        }
      }
    });
    
    // ONLY use configured targets - no defaults
    // If no targets configured, no gaps will be shown
    const targets = applicableTargets;
    
    const gaps = [];
    const activeByRole = {};
    
    // Count active staff by role
    activeShifts.forEach(shift => {
      const role = shift.role || 'unknown';
      activeByRole[role] = (activeByRole[role] || 0) + 1;
    });
    
    // Check each role against target (only for roles with configured targets)
    Object.entries(targets).forEach(([role, target]) => {
      const current = activeByRole[role] || 0;
      if (current < target) {
        const severity = current === 0 ? 'critical' : (current < target * 0.5 ? 'high' : 'medium');
        gaps.push({
          role,
          target,
          current,
          gap: target - current,
          severity,
          percentage: Math.round((current / target) * 100)
        });
      }
    });
    
    return gaps.sort((a, b) => {
      const severityOrder = { critical: 0, high: 1, medium: 2 };
      return severityOrder[a.severity] - severityOrder[b.severity];
    });
  };

  const getCallOffs = () => {
    // Find PTO schedules that were marked today or recently
    return ptoSchedules.map(pto => {
      const person = getUserForSchedule(pto);
      return {
        ...pto,
        person,
        reason: pto.notes || 'PTO'
      };
    });
  };

  // Filter schedules
  const regularShifts = schedules.filter(s => s.shift_type === 'regular');
  const onCallShifts = schedules.filter(s => s.shift_type === 'on_call');
  const ptoSchedules = schedules.filter(s => s.shift_type === 'pto')
    .sort((a, b) => {
      const nameA = (a.user?.name || a.user?.username || '').toLowerCase();
      const nameB = (b.user?.name || b.user?.username || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  
  // Active shifts right now - sorted alphabetically by user name
  const activeShifts = regularShifts
    .filter(s => isShiftActive(s))
    .sort((a, b) => {
      const nameA = (a.user?.name || a.user?.username || '').toLowerCase();
      const nameB = (b.user?.name || b.user?.username || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  const activeOnCall = onCallShifts
    .filter(s => isShiftActive(s))
    .sort((a, b) => {
      const nameA = (a.user?.name || a.user?.username || '').toLowerCase();
      const nameB = (b.user?.name || b.user?.username || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  
  // Group by role
  const groupByRole = (shifts) => {
    const grouped = {};
    shifts.forEach(shift => {
      const role = shift.role || 'unknown';
      if (!grouped[role]) grouped[role] = [];
      grouped[role].push(shift);
    });
    return grouped;
  };

  const activeByRole = groupByRole(activeShifts);
  const onCallByRole = groupByRole(activeOnCall);

  // Timeline data - group shifts by hour for visualization
  const timelineHours = Array.from({ length: 24 }, (_, i) => i);
  
  // Scroll to section function
  const scrollToSection = (ref) => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Activity className="h-12 w-12 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading today's schedule...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Today's Schedule</h1>
          <p className="text-gray-600 mt-1">
            {currentTime.toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
            <span className="mx-2">•</span>
            <span className="text-sm">
              Last updated: {getTimeSinceUpdate()}
              {isRefreshing && <span className="ml-2 text-blue-600">Refreshing...</span>}
            </span>
          </p>
        </div>
        <div className="text-right">
          <div className="text-4xl font-bold text-blue-600">{getCurrentTimeStr()}</div>
          <p className="text-sm text-gray-500">Current Time</p>
        </div>
      </div>

      {/* Current Status Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card 
          className="border-l-4 border-l-green-500 cursor-pointer transition-all hover:shadow-lg hover:scale-105"
          onClick={() => scrollToSection(activeNowRef)}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Now</p>
                <p className="text-3xl font-bold text-gray-900">{activeShifts.length}</p>
                <p className="text-xs text-gray-500 mt-1">Regular shifts</p>
              </div>
              <Users className="h-10 w-10 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border-l-4 border-l-orange-500 cursor-pointer transition-all hover:shadow-lg hover:scale-105"
          onClick={() => scrollToSection(onCallRef)}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">On-Call</p>
                <p className="text-3xl font-bold text-gray-900">{activeOnCall.length}</p>
                <p className="text-xs text-gray-500 mt-1">Available now</p>
              </div>
              <Phone className="h-10 w-10 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border-l-4 border-l-yellow-500 cursor-pointer transition-all hover:shadow-lg hover:scale-105"
          onClick={() => scrollToSection(ptoRef)}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Out Today</p>
                <p className="text-3xl font-bold text-gray-900">{ptoSchedules.length}</p>
                <p className="text-xs text-gray-500 mt-1">PTO/Time off</p>
              </div>
              <Plane className="h-10 w-10 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border-l-4 border-l-blue-500 cursor-pointer transition-all hover:shadow-lg hover:scale-105"
          onClick={() => scrollToSection(allShiftsRef)}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Shifts</p>
                <p className="text-3xl font-bold text-gray-900">{schedules.length}</p>
                <p className="text-xs text-gray-500 mt-1">All scheduled</p>
              </div>
              <Calendar className="h-10 w-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Coverage Gaps & Alerts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Coverage Gap Alerts */}
        {(() => {
          const gaps = getCoverageGaps();
          if (gaps.length === 0) return null;
          
          return (
            <Card className="border-l-4 border-l-red-500">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-red-700">
                  <AlertCircle className="h-5 w-5" />
                  <span>Coverage Gaps</span>
                  <Badge className="ml-2 bg-red-600 text-white">{gaps.length}</Badge>
                </CardTitle>
                <CardDescription>Staffing below target levels</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {gaps.map((gap) => (
                    <div key={gap.role} className={`p-3 rounded-lg ${
                      gap.severity === 'critical' ? 'bg-red-100 border-l-4 border-red-600' :
                      gap.severity === 'high' ? 'bg-orange-100 border-l-4 border-orange-600' :
                      'bg-yellow-100 border-l-4 border-yellow-600'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-gray-900">{getRoleLabel(gap.role)}</p>
                          <p className="text-sm text-gray-700">
                            {gap.current} of {gap.target} active ({gap.percentage}%)
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`text-2xl font-bold ${
                            gap.severity === 'critical' ? 'text-red-600' :
                            gap.severity === 'high' ? 'text-orange-600' :
                            'text-yellow-600'
                          }`}>
                            -{gap.gap}
                          </p>
                          <p className="text-xs text-gray-600 uppercase">{gap.severity}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Call-Offs Today */}
        {(() => {
          const callOffs = getCallOffs();
          if (callOffs.length === 0) return null;
          
          return (
            <Card className="border-l-4 border-l-purple-500">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-purple-700">
                  <User className="h-5 w-5" />
                  <span>Out Today</span>
                  <Badge className="ml-2 bg-purple-600 text-white">{callOffs.length}</Badge>
                </CardTitle>
                <CardDescription>Staff unavailable today</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {callOffs.map((callOff) => (
                    <div key={callOff.id} className="p-2 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-semibold">
                              {callOff.person.name?.charAt(0).toUpperCase() || '?'}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-sm text-gray-900">{callOff.person.name}</p>
                            <p className="text-xs text-gray-600">{callOff.reason}</p>
                          </div>
                        </div>
                        <Badge className={`text-xs ${getRoleColor(callOff.role)}`}>
                          {getRoleLabel(callOff.role)}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Yesterday Comparison - Role Breakdown */}
        {yesterdayStats && (
          <Card className="border-l-4 border-l-blue-500 md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-blue-700">
                <TrendingUp className="h-5 w-5" />
                <span>Yesterday Comparison</span>
              </CardTitle>
              <CardDescription>Role-by-role staffing vs. this time yesterday</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Total Summary */}
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border-2 border-blue-200">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Total Active</p>
                  </div>
                  <div className="flex items-center space-x-6">
                    <div className="text-center">
                      <p className="text-xs text-gray-600">Yesterday</p>
                      <p className="text-xl font-bold text-gray-900">{yesterdayStats.activeAtThisTime}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-600">Today</p>
                      <p className="text-xl font-bold text-blue-600">{activeShifts.length}</p>
                    </div>
                    <div className="text-center min-w-[60px]">
                      {(() => {
                        const diff = activeShifts.length - yesterdayStats.activeAtThisTime;
                        if (diff === 0) return <span className="text-lg text-gray-600 font-semibold">—</span>;
                        return (
                          <span className={`text-xl font-bold ${diff > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {diff > 0 ? '+' : ''}{diff}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Role Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {(() => {
                    // Get unique roles from both today and yesterday
                    const todayByRole = {};
                    activeShifts.forEach(shift => {
                      const role = shift.role || 'unknown';
                      todayByRole[role] = (todayByRole[role] || 0) + 1;
                    });
                    
                    const allRoles = new Set([
                      ...Object.keys(todayByRole),
                      ...Object.keys(yesterdayStats.byRole || {})
                    ]);
                    
                    return Array.from(allRoles).sort().map(role => {
                      const yesterdayCount = yesterdayStats.byRole?.[role] || 0;
                      const todayCount = todayByRole[role] || 0;
                      const diff = todayCount - yesterdayCount;
                      
                      return (
                        <div key={role} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-700">{getRoleLabel(role)}</p>
                          </div>
                          <div className="flex items-center space-x-4 text-sm">
                            <div className="text-center min-w-[40px]">
                              <p className="text-xs text-gray-500">Yest</p>
                              <p className="font-semibold text-gray-700">{yesterdayCount}</p>
                            </div>
                            <div className="text-center min-w-[40px]">
                              <p className="text-xs text-gray-500">Today</p>
                              <p className="font-semibold text-blue-600">{todayCount}</p>
                            </div>
                            <div className="text-center min-w-[50px]">
                              {diff === 0 ? (
                                <span className="text-gray-500 font-medium">—</span>
                              ) : (
                                <span className={`font-bold ${diff > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {diff > 0 ? '+' : ''}{diff}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>

                {/* Overall Status */}
                <div className="text-sm text-center pt-2 border-t">
                  {activeShifts.length > yesterdayStats.activeAtThisTime ? 
                    <span className="text-green-600 font-medium">✅ Better staffed than yesterday</span> :
                   activeShifts.length < yesterdayStats.activeAtThisTime ? 
                    <span className="text-red-600 font-medium">⚠️ Lower staffing than yesterday</span> :
                    <span className="text-gray-600 font-medium">➡️ Same staffing as yesterday</span>
                  }
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Upcoming Changes */}
      {(() => {
        const { starting, ending } = getUpcomingChanges();
        if (starting.length === 0 && ending.length === 0) return null;
        
        // Generate dynamic title based on configured hours
        const hoursText = endingSoonHours === 1 ? 'Next Hour' : `Next ${endingSoonHours} Hours`;
        
        return (
          <Card className="border-l-4 border-l-yellow-500">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Clock className="h-5 w-5 text-yellow-600" />
                <span>{hoursText}</span>
                <Badge className="ml-2 bg-yellow-100 text-yellow-800">UPCOMING</Badge>
              </CardTitle>
              <CardDescription>Shift changes happening soon</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Starting Soon */}
                {starting.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm text-gray-700 mb-2 flex items-center">
                      <CheckCircle className="h-4 w-4 mr-1 text-green-600" />
                      Starting Soon ({starting.length})
                    </h4>
                    <div className="space-y-2">
                      {starting.map(shift => {
                        const person = getUserForSchedule(shift);
                        const isOnCall = shift.shift_type === 'on_call';
                        const bgColor = isOnCall ? 'bg-orange-50' : 'bg-green-50';
                        const borderColor = isOnCall ? 'border-orange-200' : 'border-green-200';
                        const avatarColor = isOnCall ? 'bg-orange-600' : 'bg-green-600';
                        const badgeColor = isOnCall ? 'bg-orange-600' : 'bg-green-600';
                        
                        return (
                          <div key={shift.id} className={`p-2 border ${borderColor} rounded ${bgColor} flex items-center justify-between`}>
                            <div className="flex items-center space-x-2">
                              <div className={`w-7 h-7 ${avatarColor} rounded-full flex items-center justify-center`}>
                                <span className="text-white text-xs font-semibold">
                                  {person.name?.charAt(0).toUpperCase() || '?'}
                                </span>
                              </div>
                              <div>
                                <div className="flex items-center space-x-2">
                                  <p className="font-medium text-sm">{person.name || 'Unknown'}</p>
                                  {isOnCall && (
                                    <Badge className="bg-orange-600 text-white text-[10px] px-1 py-0">
                                      <Phone className="h-2.5 w-2.5 mr-0.5" />
                                      ON-CALL
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs text-gray-600">{formatTime(shift.start_time)}</p>
                              </div>
                            </div>
                            <Badge className={`${badgeColor} text-white text-xs`}>
                              {shift.minutesUntil}m
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Ending Soon */}
                {ending.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm text-gray-700 mb-2 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1 text-red-600" />
                      Ending Soon ({ending.length})
                    </h4>
                    <div className="space-y-2">
                      {ending.map(shift => {
                        const person = getUserForSchedule(shift);
                        const isOnCall = shift.shift_type === 'on_call';
                        const bgColor = isOnCall ? 'bg-orange-50' : 'bg-red-50';
                        const borderColor = isOnCall ? 'border-orange-200' : 'border-red-200';
                        const avatarColor = isOnCall ? 'bg-orange-600' : 'bg-red-600';
                        const badgeColor = isOnCall ? 'bg-orange-600' : 'bg-red-600';
                        
                        return (
                          <div key={shift.id} className={`p-2 border ${borderColor} rounded ${bgColor} flex items-center justify-between`}>
                            <div className="flex items-center space-x-2">
                              <div className={`w-7 h-7 ${avatarColor} rounded-full flex items-center justify-center`}>
                                <span className="text-white text-xs font-semibold">
                                  {person.name?.charAt(0).toUpperCase() || '?'}
                                </span>
                              </div>
                              <div>
                                <div className="flex items-center space-x-2">
                                  <p className="font-medium text-sm">{person.name || 'Unknown'}</p>
                                  {isOnCall && (
                                    <Badge className="bg-orange-600 text-white text-[10px] px-1 py-0">
                                      <Phone className="h-2.5 w-2.5 mr-0.5" />
                                      ON-CALL
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs text-gray-600">{formatTime(shift.end_time)}</p>
                              </div>
                            </div>
                            <Badge className={`${badgeColor} text-white text-xs`}>
                              {shift.minutesUntil}m
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Currently Active - Detailed View */}
      <Card ref={activeNowRef}>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Activity className="h-5 w-5 text-green-600" />
            <span>Currently Active</span>
            <Badge className="ml-2 bg-green-100 text-green-800">LIVE</Badge>
          </CardTitle>
          <CardDescription>Staff members working right now</CardDescription>
        </CardHeader>
        <CardContent>
          {activeShifts.length === 0 && activeOnCall.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No active shifts</h3>
              <p className="text-gray-600">No one is scheduled to work at this time.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Active Regular Shifts */}
              {Object.keys(activeByRole).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                    Regular Shifts ({activeShifts.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                    {activeShifts.map((shift) => {
                      const person = getUserForSchedule(shift);
                      return (
                        <div key={shift.id} className="p-2 border border-green-200 rounded-lg bg-green-50">
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-white text-sm font-semibold">
                                {person.name?.charAt(0).toUpperCase() || '?'}
                              </span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-sm text-gray-900 truncate">{person.name || 'Unknown'}</p>
                              <Badge className={`text-[10px] px-1 py-0 ${getRoleColor(shift.role)}`}>
                                {getRoleLabel(shift.role)}
                              </Badge>
                              <div className="flex items-center justify-between text-xs mt-1">
                                <div className="flex items-center space-x-1 text-gray-600">
                                  <Clock className="h-3 w-3" />
                                  <span className="truncate">{formatTime(shift.end_time)}</span>
                                </div>
                                <span className={`text-xs font-medium ${getTimeUntilEndColor(shift.end_time)}`}>
                                  {getTimeUntilEnd(shift.end_time)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Active On-Call */}
              {activeOnCall.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <Phone className="h-4 w-4 mr-2 text-orange-600" />
                    On-Call ({activeOnCall.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                    {activeOnCall.map((shift) => {
                      const person = getUserForSchedule(shift);
                      return (
                        <div key={shift.id} className="p-2 border border-orange-200 rounded-lg bg-orange-50">
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-white text-sm font-semibold">
                                {person.name?.charAt(0).toUpperCase() || '?'}
                              </span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-sm text-gray-900 truncate">{person.name || 'Unknown'}</p>
                              <Badge className={`text-[10px] px-1 py-0 ${getRoleColor(shift.role)}`}>
                                {getRoleLabel(shift.role)}
                              </Badge>
                              <div className="flex items-center justify-between text-xs mt-1">
                                <div className="flex items-center space-x-1 text-gray-600">
                                  <Clock className="h-3 w-3" />
                                  <span className="truncate">{formatTime(shift.end_time)}</span>
                                </div>
                                <span className={`text-xs font-medium ${getTimeUntilEndColor(shift.end_time)}`}>
                                  {getTimeUntilEnd(shift.end_time)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Full Day Schedule */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* All Regular Shifts Today */}
        <Card ref={allShiftsRef}>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Users className="h-5 w-5 text-blue-600" />
              <span>Regular Shifts ({regularShifts.length})</span>
            </CardTitle>
            <CardDescription>All scheduled regular shifts for today</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-h-96 overflow-y-auto">
              {regularShifts.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No regular shifts scheduled today</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {regularShifts
                    .sort((a, b) => {
                      const activeA = isShiftActive(a);
                      const activeB = isShiftActive(b);
                      
                      // First, sort by active status (active first)
                      if (activeA && !activeB) return -1;
                      if (!activeA && activeB) return 1;
                      
                      // Then sort alphabetically within each group
                      const nameA = (a.user?.name || a.user?.username || '').toLowerCase();
                      const nameB = (b.user?.name || b.user?.username || '').toLowerCase();
                      return nameA.localeCompare(nameB);
                    })
                    .map((shift) => {
                      const person = getUserForSchedule(shift);
                      const active = isShiftActive(shift);
                      return (
                        <div 
                          key={shift.id} 
                          className={`p-2 border rounded-lg ${active ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-white'}`}
                        >
                          <div className="flex items-center space-x-2">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center ${active ? 'bg-green-600' : 'bg-blue-600'} flex-shrink-0`}>
                              <span className="text-white text-xs font-semibold">
                                {person.name?.charAt(0).toUpperCase() || '?'}
                              </span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-sm text-gray-900 truncate">{person.name || 'Unknown'}</p>
                              <div className="flex items-center space-x-1 text-xs text-gray-500">
                                <span className="truncate">{formatTime(shift.start_time)} - {formatTime(shift.end_time)}</span>
                              </div>
                              <div className="flex items-center space-x-1 mt-1">
                                {active && <Badge className="bg-green-600 text-white text-[10px] px-1 py-0">ACTIVE</Badge>}
                                <Badge className={`text-[10px] px-1 py-0 ${getRoleColor(shift.role)}`}>
                                  {getRoleLabel(shift.role)}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* On-Call & PTO */}
        <div className="space-y-6">
          {/* On-Call Schedule */}
          <Card ref={onCallRef}>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Phone className="h-5 w-5 text-orange-600" />
                <span>On-Call ({onCallShifts.length})</span>
              </CardTitle>
              <CardDescription>On-call coverage for today</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {onCallShifts.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">No on-call scheduled today</p>
                ) : (
                  onCallShifts
                    .sort((a, b) => {
                      const activeA = isShiftActive(a);
                      const activeB = isShiftActive(b);
                      
                      // First, sort by active status (active first)
                      if (activeA && !activeB) return -1;
                      if (!activeA && activeB) return 1;
                      
                      // Then sort alphabetically within each group
                      const nameA = (a.user?.name || a.user?.username || '').toLowerCase();
                      const nameB = (b.user?.name || b.user?.username || '').toLowerCase();
                      return nameA.localeCompare(nameB);
                    })
                    .map((shift) => {
                      const person = getUserForSchedule(shift);
                      const active = isShiftActive(shift);
                      return (
                        <div 
                          key={shift.id} 
                          className={`p-3 border rounded-lg ${active ? 'border-orange-400 bg-orange-50' : 'border-gray-200 bg-white'}`}
                        >
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${active ? 'bg-orange-600' : 'bg-orange-400'}`}>
                                  <span className="text-white font-semibold">
                                    {person.name?.charAt(0).toUpperCase() || '?'}
                                  </span>
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">{person.name || 'Unknown'}</p>
                                  <p className="text-xs text-gray-500">
                                    {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                {active && <Badge className="bg-orange-600 text-white text-xs">ACTIVE</Badge>}
                                <Badge className={`text-xs ${getRoleColor(shift.role)}`}>
                                  {getRoleLabel(shift.role)}
                                </Badge>
                              </div>
                            </div>
                            {/* Phone Numbers - Prominently Displayed */}
                            <div className="pl-13 space-y-1">
                              {person.work_phone && (
                                <div className="flex items-center space-x-2 text-sm">
                                  <Phone className="h-4 w-4 text-orange-600" />
                                  <span className="font-semibold text-orange-700">Work:</span>
                                  <a href={`tel:${person.work_phone}`} className="text-blue-600 hover:underline font-medium">
                                    {person.work_phone}
                                  </a>
                                </div>
                              )}
                              {person.home_phone && (
                                <div className="flex items-center space-x-2 text-sm">
                                  <Phone className="h-4 w-4 text-gray-600" />
                                  <span className="font-semibold text-gray-700">Home:</span>
                                  <a href={`tel:${person.home_phone}`} className="text-blue-600 hover:underline font-medium">
                                    {person.home_phone}
                                  </a>
                                </div>
                              )}
                              {!person.work_phone && !person.home_phone && (
                                <p className="text-xs text-gray-500 italic">No phone number on file</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                )}
              </div>
            </CardContent>
          </Card>

          {/* PTO/Time Off */}
          <Card ref={ptoRef}>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Plane className="h-5 w-5 text-yellow-600" />
                <span>Out Today ({ptoSchedules.length})</span>
              </CardTitle>
              <CardDescription>Employees on PTO/time off</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {ptoSchedules.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">No one on PTO today</p>
                ) : (
                  ptoSchedules.map((shift) => {
                    const person = getUserForSchedule(shift);
                    const isFullDay = shift.start_time === '00:00' && shift.end_time === '23:59';
                    return (
                      <div key={shift.id} className="p-3 border border-yellow-200 rounded-lg bg-yellow-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-yellow-600 rounded-full flex items-center justify-center">
                              <span className="text-white text-sm font-semibold">
                                {person.name?.charAt(0).toUpperCase() || '?'}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{person.name || 'Unknown'}</p>
                              <p className="text-xs text-gray-500">
                                {isFullDay ? 'All Day' : `${formatTime(shift.start_time)} - ${formatTime(shift.end_time)}`}
                              </p>
                            </div>
                          </div>
                          <Badge className="bg-yellow-600 text-white text-xs">PTO</Badge>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Activities Today */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-blue-600" />
            <span>Today's Activities ({todayActivities.length})</span>
          </CardTitle>
          <CardDescription>Client visits, calls, meetings, and other scheduled activities</CardDescription>
        </CardHeader>
        <CardContent>
          {todayActivities.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {todayActivities
                .sort((a, b) => a.start_time.localeCompare(b.start_time))
                .map((activity) => {
                  const activityType = activityTypes.find(t => t.value === activity.activity_type) || {};
                  // Use enriched employee data from activity
                  const person = activity.employee || activity.user || { name: 'Unknown' };
                  
                  return (
                    <div 
                      key={activity.id} 
                      className="p-3 border rounded-lg"
                      style={{ 
                        backgroundColor: activityType.color + '10',
                        borderColor: activityType.color
                      }}
                    >
                      <div className="flex items-start space-x-3">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center text-2xl flex-shrink-0"
                          style={{ backgroundColor: activityType.color + '20' }}
                        >
                          {activityType.icon || '📅'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{activity.title}</p>
                          <p className="text-sm text-gray-600 truncate">{person.name}</p>
                          {activity.client_name && (
                            <p className="text-xs text-gray-500 truncate">Client: {activity.client_name}</p>
                          )}
                          {activity.location && (
                            <p className="text-xs text-gray-500 truncate">📍 {activity.location}</p>
                          )}
                          <div className="mt-2 flex items-center justify-between">
                            <p className="text-xs font-medium text-gray-700">
                              {formatTime(activity.start_time)} - {formatTime(activity.end_time)}
                            </p>
                            <Badge 
                              className="text-xs"
                              style={{ 
                                backgroundColor: activityType.color + '20',
                                color: activityType.color,
                                border: `1px solid ${activityType.color}`
                              }}
                            >
                              {activityType.name || 'Activity'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No activities scheduled for today</p>
              <p className="text-sm">Go to Schedule Manager to add activities</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Coverage Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-purple-600" />
            <span>Coverage by Role</span>
          </CardTitle>
          <CardDescription>Current staffing levels across all roles</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['tier1', 'tier2', 'teamlead', 'manager'].map(role => {
              const roleShifts = regularShifts.filter(s => s.role === role);
              const activeRole = roleShifts.filter(s => isShiftActive(s)).length;
              const totalRole = roleShifts.length;
              
              return (
                <div key={role} className="p-4 border rounded-lg bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <Badge className={`text-xs ${getRoleColor(role)}`}>
                      {getRoleLabel(role)}
                    </Badge>
                    <Shield className="h-4 w-4 text-gray-400" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{activeRole}/{totalRole}</div>
                  <p className="text-xs text-gray-500">Active / Total Today</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Location Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Building className="h-5 w-5 text-blue-600" />
            <span>Work Location Breakdown</span>
          </CardTitle>
          <CardDescription>Where employees are working today</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Office */}
            <div className="p-4 border-2 border-blue-200 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Building className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">In Office</p>
                    <p className="text-2xl font-bold text-blue-900">
                      {regularShifts.filter(s => s.work_location === 'office' && isShiftActive(s)).length}
                    </p>
                  </div>
                </div>
              </div>
              <div className="text-xs text-blue-700">
                {regularShifts.filter(s => s.work_location === 'office' && isShiftActive(s)).map(s => {
                  const person = getUserForSchedule(s);
                  return person?.name;
                }).filter(Boolean).join(', ') || 'No one in office'}
              </div>
            </div>

            {/* Remote */}
            <div className="p-4 border-2 border-green-200 rounded-lg bg-gradient-to-br from-green-50 to-teal-50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Home className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Remote</p>
                    <p className="text-2xl font-bold text-green-900">
                      {regularShifts.filter(s => s.work_location === 'remote' && isShiftActive(s)).length}
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-xs text-green-700">
                  <strong>Planned:</strong> {regularShifts.filter(s => s.work_location === 'remote' && s.location_status === 'planned' && isShiftActive(s)).length}
                </div>
                {regularShifts.filter(s => s.work_location === 'remote' && s.location_status === 'unplanned' && isShiftActive(s)).length > 0 && (
                  <div className="text-xs text-orange-700 flex items-center space-x-1">
                    <AlertCircle className="h-3 w-3" />
                    <span><strong>Last-minute:</strong> {regularShifts.filter(s => s.work_location === 'remote' && s.location_status === 'unplanned' && isShiftActive(s)).length}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Hybrid */}
            <div className="p-4 border-2 border-purple-200 rounded-lg bg-gradient-to-br from-purple-50 to-pink-50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <Users className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Hybrid</p>
                    <p className="text-2xl font-bold text-purple-900">
                      {regularShifts.filter(s => s.work_location === 'hybrid' && isShiftActive(s)).length}
                    </p>
                  </div>
                </div>
              </div>
              <div className="text-xs text-purple-700">
                {regularShifts.filter(s => s.work_location === 'hybrid' && isShiftActive(s)).map(s => {
                  const person = getUserForSchedule(s);
                  return person?.name;
                }).filter(Boolean).join(', ') || 'None'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Today;
